import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import BlogCard from '../components/BlogCard';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import useScrollReveal from '../hooks/useScrollReveal';
import API from '../utils/api';
import VideoSection from '../components/VideoSection';

/* ── Premium SVG icons ── */
const IconYourself = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="25" stroke="#C9A84C" strokeWidth="1.5" />
    <circle cx="26" cy="15" r="7" stroke="#2C4A3E" strokeWidth="1.8" fill="none" />
    <path d="M10 42c0-8.837 7.163-14 16-14s16 5.163 16 14" stroke="#2C4A3E" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M14 12v-4M26 30v4M38 19h4M14 19h-4" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const IconEarth = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="25" stroke="#C9A84C" strokeWidth="1.5" />
    <circle cx="26" cy="26" r="14" stroke="#2C4A3E" strokeWidth="1.8" fill="none" />
    <path d="M12 26h28M26 12c-4 4-6 8-6 14s2 10 6 14M26 12c4 4 6 8 6 14s-2 10-6 14" stroke="#2C4A3E" strokeWidth="1.5" fill="none" />
    <path d="M14 19h6l2 4-3 3h-3M32 33l2-3 4 1" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconStocks = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="25" stroke="#C9A84C" strokeWidth="1.5" />
    <path d="M12 36l8-10 6 6 8-12 6 8" stroke="#2C4A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="20" cy="26" r="2.5" fill="#C9A84C" />
    <circle cx="26" cy="32" r="2.5" fill="#C9A84C" />
    <circle cx="34" cy="20" r="2.5" fill="#C9A84C" />
    <path d="M36 16v6h-6" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconGenerosity = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="25" stroke="#C9A84C" strokeWidth="1.5" />
    <path d="M26 36s-12-8-12-16a8 8 0 0116 0 8 8 0 0116 0c0 8-12 16-12 16h-8z" stroke="#2C4A3E" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
    <path d="M26 20v10M21 25h10" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const pillars = 
[
  {
    Icon: IconYourself,
    title: 'Yourself',
    desc: 'Personal growth, health, mindset, and lifelong learning.',
    path: '/yourself',
    tag: 'Self Investment',
  },
  {
    Icon: IconEarth,
    title: 'Earth',
    desc: 'Sustainable living, conservation, and environmental impact.',
    path: '/earth',
    tag: 'Our Planet',
  },
  {
    Icon: IconStocks,
    title: 'Stocks',
    desc: 'Smart financial strategies and market wisdom for the long game.',
    path: '/stocks',
    tag: 'Financial Growth',
  },
  {
    Icon: IconGenerosity,
    title: 'Generosity',
    desc: 'Philanthropy, community giving, and an abundant mindset.',
    path: '/generosity',
    tag: 'Give Back',
  },
];

const HomePage = () => {
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();
  useScrollReveal();

  useEffect(() => {
    API.get('/blogs/page/home?limit=3').then(r => setBlogs(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <HeroSlider page="home" pageName="Welcome" />

      {/* About */}
      <section className="section">
        <div className="about-grid">
          <div className="reveal">
            <div className="section-label"><span>Our Mission</span></div>
            <h2 className="section-title">Investing in What Truly Matters</h2>
            <div className="divider" />
            <p className="section-desc">
              The Village Investors is a community-driven platform dedicated to holistic investment —
              in personal growth, environmental stewardship, financial wisdom, and generous living.
              We believe that true wealth is measured not just in portfolios, but in the legacy we leave.
            </p>
            <p className="section-desc" style={{ marginTop: 16 }}>
              Join thousands of village investors who are reshaping what it means to invest in life.
            </p>
            <div className="about-stats">
              {[
                { number: '5K+', label: 'Community Members' },
                { number: '4',   label: 'Investment Pillars' },
                { number: '200+',label: 'Blog Articles' },
                { number: '100%',label: 'Purpose-Driven' },
              ].map(s => (
                <div key={s.label} className="stat-card">
                  <span className="stat-number">{s.number}</span>
                  <span className="stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="about-image-wrap reveal reveal-delay-2">
            <div style={{
              width: '100%', aspectRatio: '4/5',
              background: 'linear-gradient(135deg, #2C4A3E 0%, #C9A84C 50%, #3D6B5B 100%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              color: 'white', gap: 16,
            }}>
              <div style={{ fontSize: '4rem', opacity: 0.8 }}>✦</div>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', textAlign: 'center', padding: '0 32px' }}>
                "The village that invests in itself is the village that thrives."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Investment Pillars ── */}
      <section className="section pillars-section" style={{ background: 'var(--cream-dark)', paddingTop: 80, paddingBottom: 80 }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }} className="reveal">
          <div className="section-label" style={{ justifyContent: 'center' }}><span>Our Pillars</span></div>
          <h2 className="section-title" style={{ margin: '0 auto' }}>Four Ways to Invest</h2>
        </div>

        <div className="pillars-grid">
          {pillars.map(({ Icon, title, desc, path, tag }, i) => (
            <div
              key={title}
              className="pillar-card reveal"
              style={{ transitionDelay: `${i * 100}ms` }}
              onClick={() => navigate(path)}
            >
              {/* top accent line */}
              <div className="pillar-accent" />

              <div className="pillar-icon-wrap">
                <Icon />
              </div>

              <span className="pillar-tag">{tag}</span>
              <h3 className="pillar-title">{title}</h3>
              <p className="pillar-desc">{desc}</p>

              <span className="pillar-cta">
                Explore
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8 }}>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Top Blogs */}
      <section className="section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 20 }}>
          <div className="reveal">
            <div className="section-label"><span>Featured Articles</span></div>
            <h2 className="section-title">Latest from the Village</h2>
          </div>
        </div>
        <div className="blogs-grid">
          {blogs.length > 0
            ? blogs.map((blog, i) => <BlogCard key={blog._id} blog={blog} delay={i * 100} />)
            : [0, 1, 2].map(i => (
                <div key={i} className="blog-card reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                  <div style={{ width: '100%', aspectRatio: '16/10', background: 'var(--cream-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--light-border)', fontSize: '2rem' }}>✦</div>
                  <div className="blog-card-body">
                    <span className="blog-card-tag">Village Insights</span>
                    <h3 className="blog-card-title">Article coming soon</h3>
                    <p className="blog-card-excerpt">Admin will add articles to this section. Check back soon for inspiring content.</p>
                  </div>
                </div>
              ))
          }
        </div>
      </section>

      <VideoSection page="home" />
      <ContactSection />
      <Footer />
    </div>
  );
};
export default HomePage;